import { ComponentProps, KeyboardEvent, useMemo, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import FocusTrap from 'focus-trap-react';
import SearchInput, { type SearchInputProps } from '@alltrails/denali/components/SearchInput';
import useScreenLock from '@alltrails/core/hooks/useScreenLock';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import Link from '@alltrails/denali/components/Link';
import { ResultBase } from '../../types/searchBoxTypes';
import { SearchIds } from '../../hooks/useSearchIds';
import CustomSearchResults from './results/CustomSearchResults';
import styles from './styles/MobileSearch.module.scss';

type MobileSearchProps<T extends ResultBase> = {
  iconButtonProps?: SearchInputProps['iconButtonProps'];
  focusTrapOptions?: ComponentProps<typeof FocusTrap>['focusTrapOptions'];
  onCancel: () => void;
  onChange: (query: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  query?: string;
  testId?: string;
  listboxId?: string;
  getOptionId?: (resultId: string) => string;
} & Pick<
  ComponentProps<typeof CustomSearchResults<T>>,
  'activeId' | 'areResultsLoading' | 'emptyState' | 'onSelect' | 'renderAdditionalContent' | 'renderResultContent' | 'results' | 'searchResultsTabBar'
>;

const MobileSearch = <T extends ResultBase>({
  iconButtonProps,
  focusTrapOptions,
  onCancel,
  onChange,
  onKeyDown,
  placeholder,
  query = '',
  testId,
  listboxId,
  getOptionId,
  activeId,
  ...customSearchResultProps
}: MobileSearchProps<T>) => {
  useScreenLock(true);

  const searchId = useId();

  const searchIds = useMemo<SearchIds | undefined>(() => {
    if (!listboxId || !getOptionId) {
      return undefined;
    }
    return {
      listboxId,
      searchLabelId: `search-label-${searchId}`,
      getOptionId
    };
  }, [listboxId, getOptionId, searchId]);

  return (
    <ModalPortal>
      <FocusTrap
        active
        // fallbackFocus is used to prevent errors in testing https://github.com/focus-trap/focus-trap-react/issues/91
        focusTrapOptions={{
          fallbackFocus: '#mobile-search',
          initialFocus: false,
          returnFocusOnDeactivate: false, // Don't want to return focus to the search input on mobile because that keeps the keyboard open when the search modal closes
          ...focusTrapOptions
        }}
      >
        <div className={styles.container}>
          <div className={styles.inputContainer} id="mobile-search">
            {searchIds && (
              <div id={searchIds.searchLabelId} className="screenReadersOnly">
                {placeholder || 'Search'}
              </div>
            )}
            <SearchInput
              autoFocus
              iconButtonProps={iconButtonProps}
              onChange={onChange}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              testId={testId}
              value={query}
              aria-autocomplete="list"
              aria-controls={listboxId}
              aria-haspopup="listbox"
              aria-expanded={true}
              aria-activedescendant={activeId && getOptionId ? getOptionId(activeId) : undefined}
              {...(searchIds && { 'aria-labelledby': searchIds.searchLabelId })}
            />
            <Link className={styles.cancel} onClick={onCancel} noUnderline>
              <FormattedMessage defaultMessage="Cancel" />
            </Link>
          </div>
          <CustomSearchResults {...customSearchResultProps} className={styles.results} query={query} searchIds={searchIds} />
        </div>
      </FocusTrap>
    </ModalPortal>
  );
};

export default MobileSearch;
